import { useRef, useState } from 'react'

import { useTranslation } from 'react-i18next'

import { Box, Flex, TertiaryButton, Text } from '@enechain/ecloud-designsystem'
import {
  FaCircleExclamationSolid,
  FaFileArrowUpRegular,
} from '@enechain/ecloud-icons'

import { iconSize } from '~/constants/iconSize'
import {
  type AcceptedCsvHeaders,
  isAcceptedCsvHeader,
} from '~/models/csvAcceptedHeaders'
import {
  decodeCsvFile,
  hasCsvDataRow,
  readCsvRecords,
} from '~/models/csvRecords'

type Props = {
  /** Step1 のガイダンスとヘッダー不一致エラーで示す列名 (日本語)。 照合には使わない。 */
  expectedHeader: string[]
  /** 受理するヘッダー (照合ルールは `AcceptedCsvHeaders`)。 */
  acceptedHeaders: AcceptedCsvHeaders
  /**
   * ガイダンスとヘッダー不一致エラーで示す列名 (common.md §ヘッダー検証
   * 「期待ヘッダーは表示言語の列名で示す」)。未指定 = `expectedHeader`。
   */
  displayHeader?: string[]
  /** ファイルサイズ上限 (bytes)。超過時は csv_content を読まず即座に拒否する。 */
  maxFileSizeBytes: number
  /**
   * データ行数上限 (ガイダンス表示用。検証はサーバー側チェックが担う)。
   * 未指定 = 行数上限なし (例: 入札 CSV。 spec 「行数の上限は設けない」)。
   * その場合ガイダンス文言から行数の記述を省く。
   */
  maxRowCount?: number
  /**
   * カラム名行の 0 起算インデックス。 既定 0 = 1 行ヘッダー、 1 = 2 行ヘッダー
   * (0 行目が説明テキスト)。 `admin/data/uploads/common.md` §1「ヘッダー行の構造」
   * が データ種別ごとに 1 行 / 2 行 のどちらかを指定するため、 検証位置を
   * 呼び出し元から注入する。
   *
   * 2026-09 時点で非既定値を渡す caller は無い (唯一の 2 行ヘッダーだったブロック
   * 入札 CSV は spec spot-bid-block-csv-import.md §1 の決定で 1 行ヘッダーへ移行済み)。 それでも
   * 残しているのは common.md §1 が 2 行ヘッダーを引き続き選択肢として定めているため。
   */
  headerRowIndex?: number
  /** サイズ・ヘッダーの両検証を通過したファイルを受け取る。 */
  onFileAccepted: (file: File) => void
  /** true の間はファイルを受け付けない (確定後に別の画面へ移るのを待つ間 等)。 */
  disabled?: boolean
}

type ValidationError =
  | { kind: 'size'; fileSizeBytes: number }
  | { kind: 'encoding' }
  | { kind: 'read' }
  | { kind: 'quote'; row: number }
  | { kind: 'header'; actualHeader: string[] }
  | { kind: 'noData' }

function bytesToMB(bytes: number): string {
  return (bytes / (1024 * 1024)).toFixed(1)
}

/**
 * ファイルサイズの上限の表記。 上限は 20MB・50MB のように整数で決めるので、 整数なら
 * 小数点を付けない (common.md §4「ファイルサイズが上限（{上限}MB）を超えています」。
 * サーバー側の文言も「20MB」)。 選択したファイルのサイズは小数第 1 位まで (bytesToMB)。
 */
function limitToMB(bytes: number): string {
  const mb = bytes / (1024 * 1024)
  return Number.isInteger(mb) ? String(mb) : mb.toFixed(1)
}

/**
 * CSV ファイル選択 (クリック + ドラッグ&ドロップ) 共通部品
 * (demand-groups-upload-tracer 設計書 §6「components/upload/」)。
 *
 * Step1 の即時検証 (design doc 画面状態マトリクス「Step1サイズ超過」「Step1ヘッダー
 * 不一致」) を担う: (1) file.size がサーバー呼び出し前に上限を超えていれば拒否
 * (AC-06)、(2) 全体を `decodeCsvFile` で文字コードを判定して復号し (UTF-8 → Shift_JIS)、 共通の `readCsvRecords` で読み、 囲みの不正を検出し、 受理するヘッダー
 * (`acceptedHeaders`) のどれかと一致するかを検証する (AC-02)。両方通過したファイルのみ `onFileAccepted` を呼ぶ
 * ため、不正なファイルでチェック API が呼ばれることはない。
 *
 * 全データ種別で再利用するため、需要グループ固有の文言・ヘッダー定義は持たず
 * `expectedHeader` / `acceptedHeaders` / `maxFileSizeBytes` で注入を受ける。
 */
export const DropZone = ({
  expectedHeader,
  acceptedHeaders,
  displayHeader,
  maxFileSizeBytes,
  maxRowCount,
  headerRowIndex = 0,
  onFileAccepted,
  disabled = false,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const shownHeader = displayHeader ?? expectedHeader
  const inputRef = useRef<HTMLInputElement>(null)
  const [error, setError] = useState<ValidationError | undefined>(undefined)
  const [isDragOver, setIsDragOver] = useState(false)
  // ヘッダー検証 (ファイル読み込み) は非同期なので、検証中は再選択を受け付けない。
  const [isValidating, setIsValidating] = useState(false)
  // 検証の世代。 完了コールバックは自分の世代が最新の時だけ onFileAccepted を
  // 呼ぶ。 isValidating による入力封鎖だけでは、 state 反映前に届いた 2 件目の
  // change / drop を取りこぼせないため、 判定は ref で決定的に行う。
  const validationGeneration = useRef(0)

  // validateAndAccept は 1 回の選択につき最大 1 回だけ onFileAccepted を呼ぶ。
  //
  // 追い越しを許すと「表示中のファイル名と、 実際に確定登録される CSV が
  // 食い違う」 状態を作れてしまう: A を選択 → 検証完了前に B を選択 → 2 本の
  // Check RPC が走り、 応答順によっては B のファイル名を表示したまま A の
  // check_token を保持できる。 確認した内容と登録内容の一致は
  // docs/principles.md の不可侵ルールなので、 世代で古い完了を捨てる。
  const validateAndAccept = (file: File): void => {
    const generation = validationGeneration.current + 1
    validationGeneration.current = generation

    if (file.size > maxFileSizeBytes) {
      setIsValidating(false)
      setError({ kind: 'size', fileSizeBytes: file.size })
      return
    }
    setIsValidating(true)
    // ファイル全体を common.md §1「文字コードの判定」の順 (UTF-8 → Shift_JIS) で復号してから
    // ヘッダーを照合する。先に文字コードを判定しないと、Shift_JIS の CSV が「ヘッダー不一致」
    // という誤った理由で拒否される。復号は BOM を除く。onFileAccepted には元の File をそのまま
    // 渡し、サーバー (csvinput.DecodeUploaded) が同じ順で判定し直す。
    void decodeCsvFile(file).then((decoded) => {
      if (generation !== validationGeneration.current) {
        return // 後続の選択に追い越された検証結果は捨てる
      }
      setIsValidating(false)
      if (decoded.kind !== 'ok') {
        setError({ kind: decoded.kind })
        return
      }
      // 囲み (ダブルクォート) の不正は閉じ忘れが末尾まで及びうるので、
      // ヘッダー行だけでなくファイル全体を見て判定する (AC-DUC-045)。
      const read = readCsvRecords(decoded.text, ',')
      if (!read.ok) {
        setError({ kind: 'quote', row: read.quoteErrorRow })
        return
      }
      const { records } = read
      // 囲みを外した後、各セルの前後の空白 (半角・全角) を除いて照合する
      // (AC-DUC-044)。カラム名行は 2 行ヘッダー = headerRowIndex 1 なら 2 行目。
      const actualHeader = (records[headerRowIndex]?.cells ?? []).map((cell) =>
        cell.trim(),
      )
      if (!isAcceptedCsvHeader(actualHeader, acceptedHeaders)) {
        setError({ kind: 'header', actualHeader })
        return
      }
      // ヘッダー行だけ・全行が空のファイルは Step1 で止める (AC-DUC-062)。
      if (!hasCsvDataRow(records, headerRowIndex)) {
        setError({ kind: 'noData' })
        return
      }
      setError(undefined)
      onFileAccepted(file)
    })
  }

  const handleFiles = (files: FileList | null): void => {
    const file = files?.[0]
    if (file !== undefined) {
      validateAndAccept(file)
    }
  }

  // 自前のヘッダー検証中は再選択を受け付けない。
  const isLocked = isValidating || disabled

  const openFileDialog = (): void => {
    if (!isLocked) {
      inputRef.current?.click()
    }
  }

  return (
    <Box>
      {/* 枠は CSV 取込 3 画面の `CsvUpload/Dropzone` と同じ (UI 辞書 components/upload-wizard.md の
          DS `UploadArea`)。 枠全体はクリックできず、 ファイルを選ぶ導線は「ファイルを選択」ボタンだけ。
          drag 中だけ背景 `Hover.lowEmphasis` / 実線の accent 枠 / ボタン disabled に変わる。 */}
      <Box
        padding={6}
        textAlign="center"
        background={isDragOver ? 'Hover.lowEmphasis' : 'Surface.secondary'}
        borderWidth="2px"
        borderStyle={isDragOver ? 'solid' : 'dashed'}
        borderColor={isDragOver ? 'Border.accentPrimary' : 'Border.midEmphasis'}
        borderRadius="base"
        aria-disabled={isLocked}
        data-testid="csv-dropzone"
        onDragLeave={() => setIsDragOver(false)}
        onDragOver={(e: React.DragEvent<HTMLDivElement>) => {
          e.preventDefault()
          if (!isLocked) setIsDragOver(true)
        }}
        onDrop={(e: React.DragEvent<HTMLDivElement>) => {
          e.preventDefault()
          setIsDragOver(false)
          if (!isLocked) handleFiles(e.dataTransfer.files)
        }}
      >
        <input
          ref={inputRef}
          accept=".csv"
          aria-label={t('common:upload.dropZone.ariaLabel')}
          disabled={isLocked}
          hidden
          onChange={(e) => {
            handleFiles(e.target.files)
            // 同じファイルを連続で選択してもイベントが発火するようにリセットする
            e.target.value = ''
          }}
          type="file"
        />
        <Flex alignItems="center" flexDirection="column" gap={2}>
          <FaFileArrowUpRegular
            boxSize={iconSize.uploadArea}
            color="Object.midEmphasis"
          />
          <Text textStyle="sectionTitle" color="Text.highEmphasis">
            {t('common:upload.dropZone.csvTitle')}
          </Text>
          <Text color="Text.midEmphasis" fontSize="md">
            {t('common:upload.dropZone.csvCondition')}
          </Text>
          <TertiaryButton
            disabled={isLocked || isDragOver}
            onClick={openFileDialog}
            size="sm"
          >
            {t('common:upload.dropZone.csvSelectButton')}
          </TertiaryButton>
          <Text color="Text.midEmphasis" fontSize="sm">
            {t('common:upload.dropZone.guidanceColumns', {
              count: shownHeader.length,
              header: shownHeader.join(','),
            })}
          </Text>
          <Text color="Text.midEmphasis" fontSize="sm">
            {maxRowCount === undefined
              ? t('common:upload.dropZone.guidanceFormatNoRowLimit', {
                  maxSizeMB: limitToMB(maxFileSizeBytes),
                })
              : t('common:upload.dropZone.guidanceFormat', {
                  maxSizeMB: limitToMB(maxFileSizeBytes),
                  maxRows: maxRowCount.toLocaleString(),
                })}
          </Text>
        </Flex>
      </Box>
      {error !== undefined && (
        <Box marginTop={2}>
          {error.kind === 'size' ? (
            <Flex alignItems="flex-start" gap={2}>
              <FaCircleExclamationSolid />
              <Text color="Text.negativeHigh" fontSize="sm">
                {t('common:upload.error.fileTooLarge', {
                  maxSizeMB: limitToMB(maxFileSizeBytes),
                  actualSizeMB: bytesToMB(error.fileSizeBytes),
                })}
              </Text>
            </Flex>
          ) : error.kind === 'quote' ? (
            <Flex alignItems="flex-start" gap={2}>
              <FaCircleExclamationSolid />
              <Text color="Text.negativeHigh" fontSize="sm">
                {t('common:upload.error.quoteInvalid', { row: error.row })}
              </Text>
            </Flex>
          ) : error.kind === 'noData' ? (
            <Flex alignItems="flex-start" gap={2}>
              <FaCircleExclamationSolid />
              <Text color="Text.negativeHigh" fontSize="sm">
                {t('common:upload.error.noDataRows')}
              </Text>
            </Flex>
          ) : error.kind === 'read' ? (
            <Flex alignItems="flex-start" gap={2}>
              <FaCircleExclamationSolid />
              <Text color="Text.negativeHigh" fontSize="sm">
                {t('common:upload.error.generic')}
              </Text>
            </Flex>
          ) : error.kind === 'encoding' ? (
            <Flex alignItems="flex-start" gap={2}>
              <FaCircleExclamationSolid />
              <Text color="Text.negativeHigh" fontSize="sm">
                {t('common:upload.error.notUtf8')}
              </Text>
            </Flex>
          ) : (
            <Flex alignItems="flex-start" flexDirection="column" gap={1}>
              <Flex alignItems="flex-start" gap={2}>
                <FaCircleExclamationSolid />
                <Text color="Text.negativeHigh" fontSize="sm">
                  {t('common:upload.error.headerMismatch', {
                    row: headerRowIndex + 1,
                  })}
                </Text>
              </Flex>
              <Text color="Text.midEmphasis" fontSize="sm">
                {t('common:upload.error.expectedHeader', {
                  header: shownHeader.join(','),
                })}
              </Text>
              <Text color="Text.midEmphasis" fontSize="sm">
                {t('common:upload.error.actualHeader', {
                  header: error.actualHeader.join(','),
                })}
              </Text>
            </Flex>
          )}
        </Box>
      )}
    </Box>
  )
}
